import React from 'react';
import { action, observable, computed, makeObservable, toJS } from 'mobx';
import { observer } from 'mobx-react';

import { FontSizes, FontWeights, TextField, IconButton, mergeStyleSets } from '@fluentui/react';
import { RangeType, toVerbForms, } from 'shared/utils/flowstep';

import { mergeSpanArray, rangeToRowSpan } from 'shared/excel/span';
import { convertResultRangeColumnIndex, getResultHeaders } from 'shared/excel/runtime/flowstepExecution';
import { capitalizeString } from 'shared/utils/utils';

import SearchableComboBox from 'src/components/base/SearchableComboBox';


const DEFAULT_RANGE_TYPE = RangeType.Selected;
const SPANS_SEPARATOR = ',';
const EMPTY_SPANS_ERROR = 'Spans cannot be empty';
const SPANS_INITIAL_STATE = {
  value: '',
  spans: [],
  error: EMPTY_SPANS_ERROR,
};

const DEFAULT_NON_EMPTY_COLUMNS = [convertResultRangeColumnIndex(0)];


const classNames = mergeStyleSets({
  rowsMode: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: '10px',
    selectors: {
      '& > :first-child': {
        alignSelf: 'flex-start',
      }
    }
  },
  optionLabel: {
    whiteSpace: 'nowrap',
    marginLeft: '25px',
  },
  icon: {
    fontSize: '24px',
  },

  nonEmptyField: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px',
    cursor: 'pointer',
    border: '1px solid transparent',

    selectors: {
      ':hover': {
        backgroundColor: '#f3f2f1'
      },
      '.result': {
        flex: 'none',
        fontSize: FontSizes.xSmall,
        fontWeight: FontWeights.semibold,
        border: '1px solid #A6A6A6',
        borderRadius: '2px',
        textAlign: 'center',
        padding: '4px',
        marginLeft: '8px',
        opacity: 1,
      },
    },
  },
});

@observer
export default class RangeSelector extends React.Component {
  @observable rangeToApplyType = DEFAULT_RANGE_TYPE;
  @observable disabled = false;
  @observable rowsState = SPANS_INITIAL_STATE;
  @observable nonEmptyColumns = DEFAULT_NON_EMPTY_COLUMNS;

  constructor(props) {
    super(props);
    makeObservable(this);

    this.spanRegexp = new RegExp('^(\\d+)(?:-(\\d+))?$', 'i');
    this.optionsToExclude = new Set(this.props.optionsToExclude || []);
    this.hideIdColumnName = !!props.hideIdColumnName;
    this.dontResetOnHeaderChange = !!props.dontResetOnHeaderChange;
  }

  @computed get isRowsMode() {
    return this.rangeToApplyType === RangeType.Rows;
  }

  @computed get errorMessage() {
    return this.isRowsMode ?  this.rowsState.error : '';
  }

  @computed get resultColumns() {
    return getResultHeaders(this.props.flowstepType)
      .filter(item => this.hideIdColumnName ? item.key !== 'id' : true)
      .map((x, index) => ({
        key: convertResultRangeColumnIndex(index), text: x.header
      }));
  }

  @computed get nonEmptyColumnsOptions() {
    return [
      ...this.resultColumns,
      ...(this.props.headers ?? []).map((x, index) => ({ key: index, text: x }))
    ];
  }

  @computed get rangeOptions() {
    const { s: verb } = toVerbForms(this.props.flowstepType);
    return [
      { key: RangeType.All, text: 'All rows',
        tooltip: `${capitalizeString(verb)} all rows from the data range this flow step is associated with`
      },
      { key: RangeType.New, text: 'Rows that are blank in the specified column',
        tooltip: `Scans data in all rows of the column specified below and only ${verb} rows that have no values in it`,
        component: this.nonEmptyColumnComponent,
      },
      { key: RangeType.Existing, text: 'Rows that are NOT blank in the specified column',
        tooltip: `Scans data in all rows of the column specified below and only ${verb} rows that have values in it`,
        component: this.nonEmptyColumnComponent,
      },
      { key: RangeType.Selected, text: 'Selected rows',
        tooltip: `Only ${verb} data in the rows the active selection spans across (multiple non-contiguous rows can be selected). Note: If this option is selected, the flow step will not be executed by the automatic scheduler`
      },
      { key: RangeType.Rows, text: 'Specific rows',
        tooltip: `Only ${verb} rows that span across the row numbers specified here. Use the crosshair icon to grab row numbers directly from the currently selected range. Multiple ranges can be listed here comma separated`,
        component: this.rowsModeComponent,
      },
    ];
  }

  @action initializeRange() {
    const { rangeToApply } = this.props;
    this.rangeToApplyType = rangeToApply ? rangeToApply.type : DEFAULT_RANGE_TYPE;
    if (this.isRowsMode) {
      this.applySpansValue(rangeToApply.spansValue || rangeToApply.spans.map(x => this.spanToString(x)).join(SPANS_SEPARATOR));
    }
    this.nonEmptyColumns = rangeToApply?.nonEmptyColumns ?? DEFAULT_NON_EMPTY_COLUMNS;
  }

  componentDidMount() {
    this.initializeRange();
  }

  componentDidUpdate(prevProps) {
    const { rangeToApply, headers, disabled, dontResetOnHeaderChange } = this.props;

    if (rangeToApply !== prevProps.rangeToApply) {
      this.initializeRange();
    }

    if (prevProps.headers && headers !== prevProps.headers && !dontResetOnHeaderChange) {
      this.nonEmptyColumns = DEFAULT_NON_EMPTY_COLUMNS;
      this.notifyRangeChanged();
    }

    disabled != this.disabled && (this.disabled = disabled);
  }

  render() {
    return (
      <SearchableComboBox
        options={this.rangeOptions}
        selectedKey={this.rangeToApplyType}
        onChange={(event, item) => this.handleSelectRangeType(item)}
        disabled={this.disabled}
      />
    );
  }

  nonEmptyColumnComponent = () => (
    <SearchableComboBox
      styles={{ root: { marginTop: '10px' } }}
      placeholder="Select column"
      selectedKey={this.nonEmptyColumns[0]}
      onChange={(event, item) => this.handleNonEmptyColumnChanged(item)}
      options={this.nonEmptyColumnsOptions}
      onRenderCell={(item, isSelected, handleItemClick) => (
        <div className={classNames.nonEmptyField} onClick={(event) => handleItemClick(event, item)}>
          <span>{item.text}</span>
          {item.key < 0 && <span className="result">RESULT</span>}
        </div>
      )}
      disabled={this.disabled}
    />
  );

  rowsModeComponent = () => (
    <div className={classNames.rowsMode}>
      <IconButton iconProps={{ iconName: 'xappex-target', styles: { root: classNames.icon } }}
        disabled={this.disabled}
        onClick={this.handleTargetClick}
      />
      <TextField
        styles={{ root: { width: '100%' } }}
        value={this.rowsState.value}
        disabled={this.disabled}
        onChange={(_, value) => this.handleRowsChanged(value)}
        onGetErrorMessage={() => this.errorMessage}
        deferredValidationTime={600}
      />
    </div>
  );

  @action handleSelectRangeType(item) {
    this.rangeToApplyType = item.key;
    this.notifyRangeChanged();
  }

  @action handleRowsChanged = (value) => {
    this.applySpansValue(value);
    this.notifyRangeChanged();
  }

  @action handleNonEmptyColumnChanged = (item) => {
    this.nonEmptyColumns = [item.key];
    this.notifyRangeChanged();
  }

  @action applySpansValue(value) {
    const rowsState = {};
    rowsState.value = value;

    const errors = [];
    rowsState.spans = value.split(SPANS_SEPARATOR).reduce((out, x) => {
      const value = x.trim();
      if (value) {
        try {
          const m = value.match(this.spanRegexp);
          if (!m) { throw new Error('span contains invalid symbols'); }

          const [, first, last ] = m;
          const index = +first - 1;
          const count = (last && last.length) ? +last - +first + 1 : 1;

          if (count < 1) { throw new Error('count of rows is < 1'); }
          if (index < 0) { throw new Error('start index should be > 1')}
          out.push({ index, count });
        }
        catch (e) {
          errors.push(`Invalid span '${value}': ${e.message}`);
        }
      }
      return out;
    }, []);

    rowsState.error = errors.join('\n');
    if (!rowsState.spans.length && !rowsState.error.length) {
      rowsState.error = EMPTY_SPANS_ERROR;
    }

    this.rowsState = rowsState;
  }

  spanToString(span) {
    return `${span.index + 1}${span.count > 1 ? `-${span.count + span.index}` : ''}`;
  }

  handleTargetClick = async () => {
    await Excel.run(async (context) => {
      const areas = context.workbook.getSelectedRanges().areas.load(['items']);
      await context.sync();

      const items = areas.items.map(x => x.load(['rowIndex', 'rowCount']));
      await context.sync();

      const values = [this.rowsState.value];
      values.push(...items.map( x => this.spanToString(rangeToRowSpan(x)) ));
      const result = values.filter(x => x !== '').join(`${SPANS_SEPARATOR} `);
      this.handleRowsChanged(result);
    });
  }

  notifyRangeChanged() {
    const isValid = !this.errorMessage;

    this.props.onChange && this.props.onChange({
      type: this.rangeToApplyType,
      spans: (this.isRowsMode && isValid) ? mergeSpanArray(this.rowsState.spans) : [],
      spansValue: this.isRowsMode ? this.rowsState.value : '',
      nonEmptyColumns: this.nonEmptyColumns ?? toJS(this.nonEmptyColumns),
    }, isValid);
  }

}
